import { Box, Divider, Stack } from '@mui/material';
import { CloudAccountType, Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { TerraformBashToggle } from '@/components/accounts/terraformBashToggle';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import { CopyableCode } from '@/components/shared/copyableCode';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useUser } from '@/contexts/useUser';

import { buildEonServiceAccountName } from './buildEonServiceAccountName';
import { getGcpSourceOrganizationCode } from './gcpCode';
import { GcpSourceAdvanceOptions } from './gcpSourceAdvanceOptions';

import { FormTextField } from '../../../../../components/form/formTextField';
import { BulletItem } from '../bulletItem';
import { DiscoveryConfigurationAccordion } from '../discoveryConfigurationAccordion';
import { extractErrorMessage } from '../errorHandling';
import { OwnCloudErrorDialog } from '../ownCloudErrorDialog';

export interface GcpConnectOrganizationState {
  connectMethod: 'terraform' | 'bash';
  onboardingFlow: boolean;
  organizationId: string;
  managementProjectId: string;
  enableGcsBucketNotificationManagement?: boolean;
  excludeProjectPatterns?: string;
}

const organizationIdConfig = getCloudFieldConfig(
  Provider.Gcp,
  'organizationId'
);

const managementProjectIdConfig = getCloudFieldConfig(
  Provider.Gcp,
  'projectId'
);

export const ConnectGcpSourceOrganization = (
  props: StepProps<GcpConnectOrganizationState>
) => {
  const [isDeploying, setIsDeploying] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { gcpControlPlane } = useEnvironment();
  const user = useUser();

  const dal = useDAL();
  const { data: body } = dal.cloudAccounts.scanning.useList();
  const scanningProjectId = body?.accounts?.find(
    (account) => account.cloudProvider === Provider.Gcp
  )?.providerAccountId;

  const code = getGcpSourceOrganizationCode(
    props.wizardState.organizationId,
    props.wizardState.managementProjectId,
    user.eonAccount?.id ?? '',
    scanningProjectId ?? '',
    gcpControlPlane,
    props.wizardState.enableGcsBucketNotificationManagement ?? false,
    props.wizardState.connectMethod,
    props.wizardState.excludeProjectPatterns
  );

  const isMissingFields =
    !props.wizardState.organizationId || !props.wizardState.managementProjectId;

  const canGoNext = () => {
    const validationErrors =
      !organizationIdConfig.validation.validateInput(
        props.wizardState.organizationId
      ) ||
      !managementProjectIdConfig.validation.validateInput(
        props.wizardState.managementProjectId
      );

    return !isDeploying && !validationErrors && !isMissingFields;
  };

  const onNextClick = async () => {
    setError(null);
    setIsDeploying(true);

    try {
      const excludePatterns = props.wizardState.excludeProjectPatterns
        ?.split(',')
        .map((p) => p.trim())
        .filter((p) => p.length > 0);
      await dal.cloudAccounts.gcpOrganization.connect({
        organizationId: props.wizardState.organizationId,
        managementServiceAccountId: buildEonServiceAccountName(
          CloudAccountType.Source,
          props.wizardState.managementProjectId,
          user.eonAccount?.id ?? '',
          scanningProjectId ?? ''
        ),
        excludeProjectPatterns:
          excludePatterns && excludePatterns.length > 0
            ? excludePatterns
            : undefined,
      });
      await props.finish();
    } catch (error: unknown) {
      const errorMessage = await extractErrorMessage(error);
      setError(new Error(errorMessage));
    } finally {
      setIsDeploying(false);
    }
  };

  const bulletTitle = `Tell us which organization you want to connect, and then
                  ${
                    props.wizardState.connectMethod === 'terraform'
                      ? 'apply the generated Terraform module'
                      : 'run the generated Bash command'
                  } in your environment.`;

  return (
    <>
      <StepContainer
        stepperIndex={3}
        hideStepper
        stepperLabels={props.stepperLabels}
        canGoNext={canGoNext}
        nextButtonDisabledTooltip={
          isDeploying
            ? 'Please wait for the deployment to complete'
            : 'Please fill valid project ID'
        }
        nextButtonText={
          props.wizardState.onboardingFlow ? 'Connect & Continue' : 'Connect'
        }
        backButtonText={'Cancel'}
        onBackClick={props.back}
        sx={{ overflow: 'auto', paddingX: '0px', paddingTop: '24px' }}
        onNextClick={onNextClick}
      >
        <Stack divider={<Divider />} gap='24px'>
          <TerraformBashToggle
            text='Before you start, make sure you can manage service accounts in your GCP environment.'
            connectMethod={props.wizardState.connectMethod}
            setConnectMethod={(connectMethod) => {
              props.setWizardState({ ...props.wizardState, connectMethod });
            }}
          />
          <Stack gap='24px' divider={<Divider />}>
            <Box padding='16px min(100px,6vw) 36px' width='100%' height='auto'>
              <BulletItem
                title={bulletTitle}
                leftContent={
                  <Stack
                    gap='24px'
                    divider={<Divider sx={{ borderStyle: 'dashed' }} />}
                  >
                    <Stack gap='24px'>
                      <FormTextField
                        title='Organization ID'
                        placeholder={organizationIdConfig.placeholder}
                        validation={organizationIdConfig.validation}
                        value={props.wizardState.organizationId || ''}
                        onChange={(event) =>
                          props.setWizardState({
                            ...props.wizardState,
                            organizationId: event.target.value,
                          })
                        }
                      />
                      <GcpSourceAdvanceOptions
                        enableGcsBucketNotificationManagement={
                          props.wizardState
                            .enableGcsBucketNotificationManagement
                        }
                        setEnableGcsBucketNotificationManagement={(
                          enableGcsBucketNotificationManagement
                        ) => {
                          props.setWizardState({
                            ...props.wizardState,
                            enableGcsBucketNotificationManagement,
                          });
                        }}
                      />
                    </Stack>
                    <FormTextField
                      title='Management project ID'
                      placeholder={managementProjectIdConfig.placeholder}
                      validation={managementProjectIdConfig.validation}
                      value={props.wizardState.managementProjectId || ''}
                      onChange={(event) =>
                        props.setWizardState({
                          ...props.wizardState,
                          managementProjectId: event.target.value,
                        })
                      }
                    />
                    {props.wizardState.connectMethod === 'bash' && (
                      <FormTextField
                        title='Exclude project patterns (optional)'
                        placeholder='e.g., internal-*, test-*'
                        value={props.wizardState.excludeProjectPatterns || ''}
                        onChange={(event) =>
                          props.setWizardState({
                            ...props.wizardState,
                            excludeProjectPatterns: event.target.value,
                          })
                        }
                      />
                    )}
                  </Stack>
                }
                rightContent={
                  <CopyableCode
                    title={
                      props.wizardState.connectMethod === 'terraform'
                        ? 'main.tf'
                        : `gcp-source-setup.sh`
                    }
                    code={code}
                    blur={isMissingFields}
                  />
                }
              />
            </Box>
            {props.wizardState.onboardingFlow && (
              <Stack gap='24px'>
                <DiscoveryConfigurationAccordion />
                <Divider />
              </Stack>
            )}
          </Stack>
        </Stack>
      </StepContainer>
      <OwnCloudErrorDialog
        isOpen={!!error}
        onClose={() => {
          setError(null);
          setIsDeploying(false);
        }}
        error={error}
      />
    </>
  );
};
